import React, { useState } from 'react';
import { Plus, Edit, Trash2, Search, Building2, CheckCircle, X, AlertCircle, Tag } from 'lucide-react';
import { toast } from 'react-toastify';
import { IncomeDepartment, CreateIncomeDepartmentData, IncomeType } from '../../lib/sdk/index';
import { getErrorMessage } from '../../lib/utils/errorMessage';
import { TableRoot, Table, THead, TR, TH, TBody, TD } from '../../components/UI/Table';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import {
  useAllIncomeDepartments,
  useCreateIncomeDepartment,
  useUpdateIncomeDepartment,
  useDeleteIncomeDepartment,
  useAllIncomeTypes,
} from '../../hooks/useQueries';

const IncomeDepartments: React.FC = () => {
  const { data: departmentsData = [], isLoading: loading, refetch: refetchDepartments } = useAllIncomeDepartments();
  const departments = Array.isArray(departmentsData) ? departmentsData : [];

  const { data: allTypesData = [] } = useAllIncomeTypes();
  const allTypes: IncomeType[] = Array.isArray(allTypesData) ? allTypesData.filter((t: any) => t.isActive) : [];

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  // CRUD modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDept, setEditingDept] = useState<IncomeDepartment | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingDept, setDeletingDept] = useState<IncomeDepartment | null>(null);

  // Type assignment modal
  const [showTypesModal, setShowTypesModal] = useState(false);
  const [typesModalDept, setTypesModalDept] = useState<IncomeDepartment | null>(null);
  const [selectedTypeIds, setSelectedTypeIds] = useState<string[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [typesSaving, setTypesSaving] = useState(false);

  const [formData, setFormData] = useState<CreateIncomeDepartmentData>({
    name: '',
    code: '',
    isActive: true,
    sortOrder: 0,
  });
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  const createMutation = useCreateIncomeDepartment({
    onSuccess: () => {
      toast.success('Income department created successfully!');
      setShowAddModal(false);
      resetForm();
      refetchDepartments();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to create income department'));
    },
  });

  const updateMutation = useUpdateIncomeDepartment({
    onSuccess: () => {
      toast.success('Income department updated successfully!');
      setShowEditModal(false);
      setEditingDept(null);
      resetForm();
      refetchDepartments();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to update income department'));
    },
  });

  const deleteMutation = useDeleteIncomeDepartment({
    onSuccess: () => {
      toast.success('Income department deleted successfully!');
      setShowDeleteConfirm(false);
      setDeletingDept(null);
      refetchDepartments();
    },
    onError: (error: any) => {
      toast.error(getErrorMessage(error, 'Failed to delete income department'));
      setShowDeleteConfirm(false);
      setDeletingDept(null);
    },
  });

  const resetForm = () => {
    setFormData({ name: '', code: '', isActive: true, sortOrder: 0 });
    setFormErrors({});
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: { [key: string]: string } = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }

    if (showEditModal && editingDept) {
      updateMutation.mutate({ id: editingDept._id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleEdit = (dept: IncomeDepartment) => {
    setEditingDept(dept);
    setFormData({ name: dept.name, code: dept.code || '', isActive: dept.isActive, sortOrder: dept.sortOrder });
    setShowEditModal(true);
  };

  const handleDelete = (dept: IncomeDepartment) => {
    setDeletingDept(dept);
    setShowDeleteConfirm(true);
  };

  const handleAssignTypes = async (dept: IncomeDepartment) => {
    setTypesModalDept(dept);
    setTypesLoading(true);
    setShowTypesModal(true);
    try {
      const { IncomeDepartmentsAPI } = await import('../../lib/sdk/index');
      const types = await IncomeDepartmentsAPI.getDepartmentTypes(dept._id);
      const ids = Array.isArray(types) ? types.map((t: any) => t._id || t) : [];
      setSelectedTypeIds(ids);
    } catch (err) {
      toast.error('Failed to load department types');
    } finally {
      setTypesLoading(false);
    }
  };

  const handleSaveTypes = async () => {
    if (!typesModalDept) return;
    setTypesSaving(true);
    try {
      const { IncomeDepartmentsAPI } = await import('../../lib/sdk/index');
      await IncomeDepartmentsAPI.setDepartmentTypes(typesModalDept._id, selectedTypeIds);
      toast.success('Type assignments saved successfully!');
      setShowTypesModal(false);
      setTypesModalDept(null);
    } catch (err: any) {
      toast.error(getErrorMessage(err, 'Failed to save type assignments'));
    } finally {
      setTypesSaving(false);
    }
  };

  const toggleTypeSelection = (typeId: string) => {
    setSelectedTypeIds((prev) =>
      prev.includes(typeId) ? prev.filter((id) => id !== typeId) : [...prev, typeId]
    );
  };

  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setEditingDept(null);
    resetForm();
  };

  const filteredDepartments = departments.filter((dept) => {
    const matchesSearch =
      dept.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (dept.code || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && dept.isActive) ||
      (statusFilter === 'inactive' && !dept.isActive);
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Income Departments</h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage income departments and assign income types to each department
          </p>
        </div>
        <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
          Add Department
        </Button>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
              <Building2 className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Departments</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{departments.length}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Active</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {departments.filter((d) => d.isActive).length}
              </p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 dark:bg-red-900 rounded-lg">
              <X className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Inactive</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {departments.filter((d) => !d.isActive).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="card">
        <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search departments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="form-select"
            >
              <option value="all">All</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
            {(statusFilter !== 'all' || searchTerm) && (
              <Button variant="secondary" size="sm" onClick={() => { setStatusFilter('all'); setSearchTerm(''); }}>
                Clear Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {filteredDepartments.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
              {departments.length === 0 ? 'No income departments found' : 'No departments match your filters'}
            </h3>
          </div>
        ) : (
          <TableRoot>
            <Table>
              <THead>
                <TR>
                  <TH>Name</TH>
                  <TH>Code</TH>
                  <TH>Status</TH>
                  <TH>Sort Order</TH>
                  <TH>Created At</TH>
                  <TH>Actions</TH>
                </TR>
              </THead>
              <TBody>
                {filteredDepartments.map((dept) => (
                  <TR key={dept._id} className={!dept.isActive ? 'bg-gray-50 dark:bg-gray-800/50' : ''}>
                    <TD className="font-medium">{dept.name}</TD>
                    <TD className="text-gray-500 font-mono text-sm">{dept.code || '—'}</TD>
                    <TD>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          dept.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                        }`}
                      >
                        {dept.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </TD>
                    <TD className="text-gray-500">{dept.sortOrder}</TD>
                    <TD className="text-sm text-gray-500">{formatDate(dept.createdAt)}</TD>
                    <TD>
                      <div className="flex space-x-2 flex-wrap gap-y-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(dept)} leftIcon={<Edit className="h-4 w-4" />}>
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAssignTypes(dept)}
                          leftIcon={<Tag className="h-4 w-4" />}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          Types
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(dept)}
                          leftIcon={<Trash2 className="h-4 w-4" />}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </Button>
                      </div>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableRoot>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || showEditModal) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {showAddModal ? 'Add Income Department' : 'Edit Income Department'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Zakat Collection"
                  className={formErrors.name ? 'border-red-500' : ''}
                />
                {formErrors.name && <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Code</label>
                <Input
                  type="text"
                  value={formData.code || ''}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="e.g., ZKT"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sort Order</label>
                <Input
                  type="number"
                  value={formData.sortOrder?.toString() || '0'}
                  onChange={(e) => setFormData({ ...formData, sortOrder: parseInt(e.target.value) || 0 })}
                  min="0"
                />
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive !== false}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Active
                </label>
              </div>
              <div className="flex space-x-3 pt-4">
                <Button type="button" variant="secondary" onClick={closeModal} className="flex-1">
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                  className="flex-1"
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? 'Saving...'
                    : showAddModal
                    ? 'Add Department'
                    : 'Update Department'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {showDeleteConfirm && deletingDept && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delete Income Department</h2>
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete "{deletingDept.name}"? This cannot be undone. Departments with income
              records cannot be deleted.
            </p>
            <div className="flex space-x-3">
              <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setDeletingDept(null); }} className="flex-1">
                Cancel
              </Button>
              <Button variant="error" onClick={() => deletingDept && deleteMutation.mutate(deletingDept._id)} className="flex-1">
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Type Assignment Modal */}
      {showTypesModal && typesModalDept && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Assign Income Types — {typesModalDept.name}
              </h2>
              <Button variant="ghost" size="sm" onClick={() => { setShowTypesModal(false); setTypesModalDept(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Select which income types are available for this department when recording income.
            </p>
            {typesLoading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner />
              </div>
            ) : allTypes.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No active income types found. Add income types first.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                {allTypes.map((type) => (
                  <label
                    key={type._id}
                    className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTypeIds.includes(type._id)}
                      onChange={() => toggleTypeSelection(type._id)}
                      className="rounded border-gray-300 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{type.name}</span>
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <span className="text-xs text-gray-500">
                {selectedTypeIds.length} of {allTypes.length} selected
              </span>
              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => { setShowTypesModal(false); setTypesModalDept(null); }}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSaveTypes} disabled={typesSaving || typesLoading}>
                  {typesSaving ? 'Saving...' : 'Save Assignments'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncomeDepartments;
